import { useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";

const PACKAGE_DAYS = {
  "1W": 7,
  "2W": 14,
  "4W": 28,
};

export default function PackageModal({ patient, onClose, onSuccess }) {
  const [packageType, setPackageType] = useState("");
  const [packageAmount, setPackageAmount] = useState("");
  const [paidNow, setPaidNow] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [packageStartDate, setPackageStartDate] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const processingRef = useRef(false);

  const handleCreatePackage = async () => {
    if (processingRef.current) return;

    processingRef.current = true;

    if (!patient) {
      processingRef.current = false;
      return;
    }

    // 🔴 1️⃣ Check previous package dues
    const pkgQuery = query(
      collection(db, "packages"),
      where("vpjc_id", "==", patient.vpjc_id),
    );

    const pkgSnap = await getDocs(pkgQuery);

    const packageWithDue = pkgSnap.docs.find((doc) => {
      const p = doc.data();
      return Number(p.due_amount || 0) > 0;
    });

    if (packageWithDue) {
      const due = packageWithDue.data().due_amount || 0;
      setError(`Previous package due ₹${due}. Please clear payment first.`);
      return;
    }

    // 🔴 2️⃣ Check active package
    const activeQuery = query(
      collection(db, "packages"),
      where("vpjc_id", "==", patient.vpjc_id),
      where("status", "==", "ACTIVE"),
    );

    const activeSnap = await getDocs(activeQuery);

    if (!activeSnap.empty) {
      setError("Patient already has an active package.");
      return;
    }

    if (!packageType) {
      setError("Please select package duration");
      return;
    }

    if (!packageAmount || Number(packageAmount) <= 0) {
      setError("Enter valid package amount");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const branch =
        typeof patient.branch_name === "object"
          ? patient.branch_name.name
          : patient.branch_name;
      const totalDays = PACKAGE_DAYS[packageType];

      const startDate = packageStartDate
        ? new Date(packageStartDate)
        : new Date();

      startDate.setHours(0, 0, 0, 0);

      const expiryDate = new Date(startDate);
      expiryDate.setDate(startDate.getDate() + totalDays - 1);

      const createdDate = new Date();
      createdDate.setHours(0, 0, 0, 0);

      let initialUsedDays = 0;

      // Check if patient already has any visit today
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      const visitSnap = await getDocs(
        query(
          collection(db, "visits"),
          where("vpjc_id", "==", patient.vpjc_id),
        ),
      );

      const hasVisitToday = visitSnap.docs.some((d) => {
        const visit = d.data();

        const visitDate = visit.created_at?.toDate
          ? visit.created_at.toDate()
          : new Date(visit.created_at);

        visitDate.setHours(0, 0, 0, 0);

        return visitDate.getTime() === today.getTime();
      });

      if (hasVisitToday) {
        initialUsedDays = 1;
      }

      // 💰 BILLING CALCULATION (MAIN FIX)
      const totalAmount = Number(packageAmount);
      const paidNowAmount = Number(paidNow || 0);

      if (paidNowAmount > totalAmount) {
        setError("Paid amount cannot be greater than total package amount");
        setLoading(false);
        return;
      }

      if (paidNowAmount > 0 && !paymentMode) {
        setError("Please select payment mode");
        setLoading(false);
        return;
      }
      const dueAmount = Math.max(totalAmount - paidNowAmount, 0);

      let paymentStatus = "UNPAID";
      if (paidNowAmount === 0) paymentStatus = "UNPAID";
      else if (dueAmount === 0) paymentStatus = "PAID";
      else paymentStatus = "PARTIAL";

      // 1️⃣ Create Package (CORRECT BILLING STRUCTURE)
      const packageRef = await addDoc(collection(db, "packages"), {
        patient_id: patient.id,
        vpjc_id: patient.vpjc_id,
        branch_name: branch,
        // 📦 Package Info
        duration_weeks: totalDays / 7,
        total_days: totalDays,
        total_amount: totalAmount,

        // 💰 PAYMENT SYSTEM (FIXED)
        paid_amount: paidNowAmount,
        due_amount: dueAmount,
        payment_mode: paymentMode || "",
        consultation_payment_status: paymentStatus,

        // 📅 Dates
        start_date: startDate,
        expiry_date: expiryDate,
        status: "ACTIVE",
        created_at: new Date(),
      });

      // 2️⃣ If admin collected money today → store receipt log (VERY IMPORTANT)
      if (paidNowAmount > 0) {
        await addDoc(collection(db, "package_payments"), {
          package_id: packageRef.id,
          patient_id: patient.id,
          vpjc_id: patient.vpjc_id,
          amount: paidNowAmount,
          payment_mode: paymentMode,
          branch_name: branch,
          created_at: new Date(),
        });
      }
      // 2️⃣ Create PACKAGE PURCHASE visit (PERMANENT ROW IN VISITS TABLE)
      await addDoc(collection(db, "visits"), {
        patient_id: patient.id,
        vpjc_id: patient.vpjc_id,
        name: patient.name || "-",
        mobile: patient.mobile || "-",
        branch_name: branch,
        token_number: "PKG",
        visit_date: startDate,

        treatment: "Package Purchase",
        payment_mode: paymentMode || "",
        consultation_payment_status: "PACKAGE_PURCHASE",
        consultation_fee: paidNowAmount,

        created_at: new Date(),
      });
      // 3️⃣ Update patient active package snapshot
      // 3️⃣ Update patient active package snapshot (AND TREATMENT STATUS)
      await updateDoc(doc(db, "patients", patient.id), {
        active_package: true,
        active_package_id: packageRef.id,
        package_status: "ACTIVE",

        package_type: packageType,
        package_total_days: totalDays,

        package_used_days: initialUsedDays,

        package_start_date: startDate,
        package_expiry_date: expiryDate,
        updated_at: new Date(),
      });

      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Package creation error:", err);
      setError("Failed to create package. Try again.");
    } finally {
      processingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-[95%] max-w-md shadow-lg">
        <h3 className="text-lg font-semibold text-[#204080] mb-5">
          Add Therapy Package
        </h3>

        {/* Package Type */}
        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-600">
            Package Duration *
          </label>
          <select
            value={packageType}
            onChange={(e) => {
              setPackageType(e.target.value);
              setError("");
            }}
            className="w-full mt-1 border rounded-lg px-3 py-2"
          >
            <option value="">Select Package</option>
            <option value="1W">1 Week (7 Sessions)</option>
            <option value="2W">2 Weeks (14 Sessions)</option>
            <option value="4W">4 Weeks (28 Sessions)</option>
          </select>
        </div>

        {/* Amount */}
        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-600">
            Package Amount (₹) *
          </label>
          <input
            type="number"
            value={packageAmount}
            onChange={(e) => {
              setPackageAmount(e.target.value);
              setError("");
            }}
            onWheel={(e) => e.target.blur()} // 🔥 Prevent scroll change
            placeholder="Enter amount"
            className="w-full mt-1 border rounded-lg px-3 py-2"
          />
        </div>
        {/* Paid Now */}
        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-600">
            Amount Paid Now (₹)
          </label>
          <input
            type="number"
            value={paidNow}
            onChange={(e) => setPaidNow(e.target.value)}
            onWheel={(e) => e.target.blur()} // 🔥 Important
            placeholder="Enter paid amount (can be partial)"
            className="w-full mt-1 border rounded-lg px-3 py-2"
          />

          <p className="text-xs text-gray-500 mt-1">
            Leave empty if not paid today
          </p>
        </div>
        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-600">
            Payment Mode
          </label>

          <select
            value={paymentMode}
            onChange={(e) => setPaymentMode(e.target.value)}
            disabled={Number(paidNow || 0) <= 0}
            className={`w-full mt-1 border rounded-lg px-3 py-2
      ${
        Number(paidNow || 0) <= 0
          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
          : paymentMode === "ONLINE"
            ? "text-blue-700 border-blue-300"
            : paymentMode === "CASH"
              ? "text-green-700 border-green-300"
              : ""
      }`}
          >
            <option value="">Select</option>
            <option value="CASH">Cash</option>
            <option value="ONLINE">Online</option>
          </select>

          <p className="text-xs text-gray-500 mt-1">
            Required if payment is collected
          </p>
        </div>
        {/* Start Date */}
        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-600">
            Start Date
          </label>
          <input
            type="date"
            value={packageStartDate}
            onChange={(e) => setPackageStartDate(e.target.value)}
            className="w-full mt-1 border rounded-lg px-3 py-2"
          />
          <p className="text-xs text-gray-500 mt-1">
            Default: Today (if not selected)
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-3 text-sm text-red-600 font-medium">{error}</div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border"
            disabled={loading}
          >
            Cancel
          </button>

          <button
            onClick={handleCreatePackage}
            disabled={loading}
            className="px-5 py-2 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-60"
          >
            {loading ? "Creating..." : "Create Package"}
          </button>
        </div>
      </div>
    </div>
  );
}
